// @systeme: interface
// La boîte de réception : les fiches à valider, capture par capture. Se vide au rythme
// de l'utilisatrice, jamais sous pression.
import { useCallback, useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { ChevronLeft } from 'lucide-react';
import { Link as LienRouteur } from 'react-router';
import type { BoiteDeReception, FicheProposee } from '@foyer/shared';
import { CarteFiche } from '../composants/CarteFiche';
import { Chargement } from '../composants/Chargement';
import { useMessages } from '../composants/Messages';
import { api } from '../lib/api';
import { tailles } from '../theme/theme';

export function Boite() {
  const { direErreur } = useMessages();
  const [boite, setBoite] = useState<BoiteDeReception | undefined>();

  const charger = useCallback(async () => {
    try {
      setBoite(await api.captures.boite());
    } catch (e) {
      direErreur(e);
    }
  }, [direErreur]);

  useEffect(() => {
    api.captures.boite().then(setBoite).catch(direErreur);
  }, [direErreur]);

  // Tant qu'une capture est en cours de traitement, on regarde régulièrement.
  useEffect(() => {
    if (!boite || boite.nombreEnCours === 0) return;
    const minuterie = setInterval(() => void charger(), 3000);
    return () => clearInterval(minuterie);
  }, [boite, charger]);

  function remplacer(fiche: FicheProposee) {
    setBoite(
      (b) =>
        b && {
          ...b,
          captures: b.captures.map((c) => ({
            ...c,
            fiches: c.fiches.map((f) => (f.id === fiche.id ? fiche : f)),
          })),
        },
    );
  }

  if (!boite) return <Chargement />;

  return (
    <Box sx={{ px: `${tailles.marge}px`, pt: 3 }}>
      <Button
        component={LienRouteur}
        to="/"
        startIcon={<ChevronLeft size={18} />}
        color="inherit"
        sx={{ px: 0, mb: 1 }}
      >
        Aujourd’hui
      </Button>
      <Typography variant="h1" component="h1" sx={{ mb: 2 }}>
        À valider
      </Typography>

      {boite.captures.length === 0 && (
        <Box sx={{ py: 5, textAlign: 'center' }}>
          <Typography variant="h2" component="p" color="primary.dark">
            Rien à valider.
          </Typography>
          <Typography color="text.secondary" sx={{ mt: 1 }}>
            Tout est rangé.
          </Typography>
        </Box>
      )}

      {boite.captures.map((capture) => (
        <Box key={capture.id} sx={{ mb: 3 }}>
          {capture.documents.map((d) => (
            <Box key={d.id} sx={{ mb: 1 }}>
              <Box
                component="img"
                src={api.documents.urlFichier(d.id)}
                alt={d.sensibilite === 'sante' ? 'Document de santé' : 'Document photographié'}
                sx={{ maxHeight: 160, maxWidth: '100%', borderRadius: 2, display: 'block' }}
              />
            </Box>
          ))}
          {capture.transcription && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontStyle: 'italic' }}>
              {capture.type === 'image' ? 'J’ai lu : « ' : 'Tu as dit : « '}
              {capture.transcription.length > 160
                ? `${capture.transcription.slice(0, 157)}…`
                : capture.transcription}{' '}
              »
            </Typography>
          )}
          {(capture.statut === 'en_attente' ||
            capture.statut === 'transcription' ||
            capture.statut === 'analyse') && (
            <Typography color="text.secondary" sx={{ mb: 1 }} aria-live="polite">
              {capture.statut === 'transcription'
                ? 'J’écoute…'
                : capture.statut === 'analyse'
                  ? 'Je réfléchis…'
                  : 'Je m’en occupe…'}
            </Typography>
          )}
          {capture.statut === 'erreur' && (
            <Stack spacing={1} sx={{ mb: 1 }}>
              <Typography>{capture.erreur}</Typography>
              <Stack direction="row" spacing={1}>
                {capture.transcription && (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() =>
                      void api.captures.retraiter(capture.id).then(charger).catch(direErreur)
                    }
                  >
                    Réessayer
                  </Button>
                )}
                <Button
                  color="inherit"
                  size="small"
                  onClick={() =>
                    void api.captures.supprimer(capture.id).then(charger).catch(direErreur)
                  }
                >
                  Supprimer
                </Button>
              </Stack>
            </Stack>
          )}
          {capture.statut === 'prete' && capture.erreur && (
            <Typography color="text.secondary" sx={{ mb: 1 }}>
              {capture.erreur}
            </Typography>
          )}
          {capture.fiches.map((fiche) => (
            <CarteFiche
              key={fiche.id}
              fiche={fiche}
              onChangee={remplacer}
              onTerminee={() => void charger()}
            />
          ))}
        </Box>
      ))}
    </Box>
  );
}
